import { Placement } from '@popperjs/core';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { useOpenStore } from 'hooks/useOpenStore';
import { OpenStore } from 'stores/shared/OpenStore';

import PopperPanel from '../PopperPanel';
import Typo, { TypoView } from '../Typo';

import s from './ToolTip.module.scss';

export type ToolTipProps = {
  popperClassName?: string;
  position?: Placement;
  text: React.ReactNode;
  textView?: TypoView;
  targetRef: React.RefObject<HTMLElement>;
  controller?: OpenStore;
  sameWidth?: boolean;
  offset?: number;
};

const DEFAULT_OFFSET = 8;

const Tooltip: React.FC<ToolTipProps> = ({
  popperClassName,
  text,
  textView = 'body-1',
  targetRef,
  position = 'top',
  controller,
  sameWidth = false,
  offset = DEFAULT_OFFSET,
}) => {
  const { isOpen, close, open } = useOpenStore(!!controller);

  const options = React.useMemo(() => {
    return {
      placement: position,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, offset],
          },
        },
      ],
    };
  }, [position, offset]);

  React.useEffect(() => {
    const ref = targetRef.current;

    if (controller === undefined) {
      ref?.addEventListener('mouseenter', open);
      ref?.addEventListener('mouseleave', close);

      return () => {
        ref?.removeEventListener('mouseenter', open);
        ref?.removeEventListener('mouseleave', close);
      };
    }
  }, [close, open, targetRef, controller]);

  return (
    <PopperPanel
      options={options}
      popperClassName={cn(popperClassName, s.tooltip)}
      open={isOpen}
      referenceElement={targetRef.current}
      sameWidth={sameWidth}
      withShadow
    >
      <Typo view={textView} block>
        {text}
      </Typo>
    </PopperPanel>
  );
};

export default observer(Tooltip);
